import { useEffect, useRef, useState } from "react";
import { api, streamNatalie, synthesizeSpeech } from "@/lib/api";
import { Mic, Send, Square, Volume2, VolumeX, Loader2 } from "lucide-react";

const NATALIE_AVATAR = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBBSSUyMGF2YXRhciUyMGZlbWFsZSUyMGFnZW50JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2MTA4NDIzfDA&ixlib=rb-4.1.0&q=85";

export function NatalieChat({ caseId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [autoVoice, setAutoVoice] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const seenProactive = useRef(new Set());
  const autoVoiceRef = useRef(true);
  useEffect(() => { autoVoiceRef.current = autoVoice; }, [autoVoice]);

  // Load history once; mark existing proactive messages as already seen so we
  // don't re-announce them on mount.
  useEffect(() => {
    api.get(`/cases/${caseId}/natalie/history`).then((r) => {
      r.data.forEach((m) => { if (m.proactive && m.created_at) seenProactive.current.add(m.created_at); });
      setMessages(r.data.map((m) => ({ role: m.role, text: m.text, proactive: m.proactive })));
    }).catch(() => {});
  }, [caseId]);

  // Proactive polling: Natalie announces new breakthroughs the engines surface.
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/cases/${caseId}/natalie/history`);
        if (!active) return;
        const fresh = data.filter((m) => m.proactive && m.created_at && !seenProactive.current.has(m.created_at));
        if (fresh.length) {
          fresh.forEach((m) => seenProactive.current.add(m.created_at));
          setMessages((prev) => [...prev, ...fresh.map((m) => ({ role: "natalie", text: m.text, proactive: true }))]);
          speak(fresh[fresh.length - 1].text);
        }
      } catch { /* ignore */ }
    };
    const t = setInterval(poll, 5000);
    return () => { active = false; clearInterval(t); };
  }, [caseId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const stopSpeaking = () => { try { audioRef.current?.pause(); } catch {} setSpeaking(false); };

  // High-fidelity female neural voice (OpenAI TTS · Shimmer) via the backend.
  const speak = async (text) => {
    if (!autoVoiceRef.current || !text?.trim()) return;
    try {
      const { audio } = await synthesizeSpeech(text.slice(0, 4096));
      if (!audio || !autoVoiceRef.current) return;
      stopSpeaking();
      const a = new Audio(`data:audio/mp3;base64,${audio}`);
      audioRef.current = a;
      a.onended = () => setSpeaking(false);
      setSpeaking(true);
      await a.play();
    } catch { setSpeaking(false); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "investigator", text: msg }, { role: "natalie", text: "" }]);
    setStreaming(true);
    let acc = "";
    try {
      await streamNatalie(caseId, msg, (tok) => {
        acc += tok;
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "natalie", text: acc }; return c; });
      });
      speak(acc);
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "natalie", text: "I've lost the line to Monk for a moment. Try again." }; return c; });
    } finally { setStreaming(false); }
  };

  const toggleRecord = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "voice.webm");
        try {
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) await send(data.text.trim());
        } catch { /* ignore */ }
        finally { setTranscribing(false); }
      };
      mr.start(); mediaRef.current = mr; setRecording(true);
    } catch { setRecording(false); }
  };

  return (
    <div className="flex flex-col h-full bg-monk-panel border border-white/10 rounded-md overflow-hidden" data-testid="natalie-chat">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 bg-black/40">
        <div className="relative">
          <img src={NATALIE_AVATAR} alt="Natalie" className="w-9 h-9 rounded-md object-cover border border-white/10" />
          {speaking && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-monk-blue border-2 border-monk-panel animate-pulse" />}
        </div>
        <div className="leading-tight flex-1">
          <div className="font-heading font-700 text-sm tracking-tight">NATALIE</div>
          <div className="overline">{speaking ? "Speaking…" : "The Interface · Monk's Partner"}</div>
        </div>
        <button onClick={() => { const nv = !autoVoice; setAutoVoice(nv); if (!nv) stopSpeaking(); }} data-testid="natalie-voice-toggle"
          title={autoVoice ? "Auto-voice on — click to mute" : "Muted — click for auto-voice"}
          className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors ${autoVoice ? "border-monk-blue/40 text-monk-blue" : "border-white/10 text-neutral-500"}`}>
          {autoVoice ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[280px]">
        {messages.length === 0 && (
          <div className="text-center text-neutral-500 text-sm mt-8 px-4">
            <p className="mb-1 text-neutral-300">I'm Natalie.</p>
            <p className="hunch-data">I'll watch the engines for you and speak up the moment Monk finds something. Ask me anything about this case.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "investigator" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
            <div className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "investigator" ? "bg-monk-blue text-white" : m.proactive ? "bg-monk-blue/10 border border-monk-blue/30 text-neutral-100" : "bg-black/50 border border-white/10 text-neutral-200"}`}>
              {m.proactive && <div className="overline text-monk-blue mb-1" data-testid="proactive-tag">Natalie · Breakthrough</div>}
              {m.text || (streaming && i === messages.length - 1 ? <span className="text-neutral-500 font-mono text-xs">Natalie is relaying Monk…</span> : "")}
            </div>
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-white/10 bg-black/40">
        <div className="flex items-end gap-2">
          <button onClick={toggleRecord} disabled={transcribing || streaming} data-testid="natalie-mic"
            className={`w-10 h-10 shrink-0 rounded-md border flex items-center justify-center transition-colors ${recording ? "border-monk-critical bg-monk-critical/15 text-monk-critical" : "border-white/15 text-neutral-400 hover:text-white"}`}>
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <textarea data-testid="natalie-input" rows={1} value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={recording ? "Listening…" : "Message Natalie…"}
            className="flex-1 resize-none bg-black border border-white/15 rounded-md px-3 py-2.5 text-sm focus:border-monk-blue focus:outline-none transition-colors max-h-24" />
          <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="natalie-send"
            className="w-10 h-10 shrink-0 rounded-md bg-monk-blue text-white flex items-center justify-center hover:bg-monk-blue/90 disabled:opacity-40 transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
